import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { useLang } from "../i18n";
import { api, API } from "../api";

const TeachVideo = ({ videoId }) => {
  const token = localStorage.getItem("el_token");
  return <video src={`${API}/videos/${videoId}/stream?auth=${token}`} controls preload="metadata"
    className="w-full h-full rounded-xl bg-black" data-testid="teach-video-file" />;
};

export default function TeachingMode() {
  const { topicId } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(1);

  useEffect(() => { api.get(`/topics/${topicId}`).then((r) => setTopic(r.data)); }, [topicId]);

  const slides = useMemo(() => {
    if (!topic) return [];
    const paras = (topic.content_en || "").split(/\n+|(?<=\.)\s{2,}/).filter((p) => p.trim().length > 20);
    const list = [
      { kind: "title" },
      ...paras.slice(0, 4).map((p) => ({ kind: "text", body: p })),
      { kind: "words" },
    ];
    if (topic.video_url || topic.video_id) list.push({ kind: "video" });
    list.push({ kind: "practice" });
    return list;
  }, [topic]);

  if (!topic) return <div className="min-h-screen grid place-items-center text-slate-400">{t("loading")}</div>;
  const s = slides[i] || slides[0];

  const embed = (url) => {
    const m = String(url).match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : url;
  };

  return (
    <div className="min-h-screen bg-white text-[#0b1b46]" style={{ fontSize: `${zoom}rem` }}>
      <header className="border-b border-slate-200 h-14 flex items-center px-4 gap-3 sticky top-0 bg-white/90 backdrop-blur-xl z-20">
        <button data-testid="exit-teaching-btn" onClick={() => navigate("/teacher")}
          className="pro-btn bg-slate-100 text-[#0b1b46] flex items-center gap-1 text-sm"><X size={16} /></button>
        <div className="font-bold">{topic.title_en} <span className="text-slate-400">· {topic.title_ar}</span></div>
        <div className="ms-auto flex items-center gap-2">
          <button data-testid="zoom-out-btn" onClick={() => setZoom((z) => Math.max(0.8, z - 0.1))}
            className="pro-btn bg-slate-100 text-[#0b1b46]"><ZoomOut size={16} /></button>
          <button data-testid="zoom-in-btn" onClick={() => setZoom((z) => Math.min(1.6, z + 0.1))}
            className="pro-btn bg-slate-100 text-[#0b1b46]"><ZoomIn size={16} /></button>
          <span className="text-sm font-bold text-slate-500" data-testid="slide-counter">
            {t("slide")} {i + 1}/{slides.length} · {Math.round(zoom * 100)}%</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.28 }}
            className="rounded-2xl border-2 border-slate-200 p-8 sm:p-14 min-h-[60vh] flex flex-col justify-center"
            data-testid="teaching-slide">
            {s.kind === "title" && (
              <div>
                <div className="text-xs tracking-[0.3em] uppercase font-bold" style={{ color: "var(--pro-secondary)" }}>
                  {topic.reference_book} · {t("grade")} {topic.grade}</div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mt-4">{topic.title_en}</h1>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-500 mt-2">{topic.title_ar}</h2>
                <p className="mt-8 text-lg text-slate-600 max-w-3xl leading-relaxed">{topic.summary_ar}</p>
              </div>
            )}
            {s.kind === "text" && (
              <p dir="ltr" className="text-2xl sm:text-3xl leading-relaxed font-semibold max-w-4xl text-start">{s.body}</p>
            )}
            {s.kind === "words" && (
              <div>
                <h2 className="text-3xl font-black mb-8">{t("vocabulary")}</h2>
                <div className="grid sm:grid-cols-3 gap-6">
                  {(topic.words || []).map((w) => (
                    <div key={w} className="rounded-xl border-2 border-slate-200 p-6 text-center">
                      <div className="text-2xl font-bold">{w}</div>
                      <button data-testid={`speak-${w}`} className="text-sm mt-2 font-bold"
                        style={{ color: "var(--pro-secondary)" }}
                        onClick={() => { const u = new SpeechSynthesisUtterance(w); u.lang = "en-US"; speechSynthesis.speak(u); }}>
                        🔊 listen</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {s.kind === "video" && (
              <div className="aspect-video w-full">
                {topic.video_id ? (
                  <TeachVideo videoId={topic.video_id} />
                ) : (
                  <iframe title="lesson" src={embed(topic.video_url)} className="w-full h-full rounded-xl"
                    allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
                )}
              </div>
            )}
            {s.kind === "practice" && (
              <div>
                <h2 className="text-3xl font-black mb-6">Practice together</h2>
                <ul className="space-y-4 text-xl">
                  {(topic.words || []).slice(0, 4).map((w) => (
                    <li key={w} className="border-b border-dashed border-slate-300 pb-3">
                      Make a sentence with <b>{w}</b> ______________
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between items-center mt-8">
          <button data-testid="prev-slide-btn" disabled={i === 0} onClick={() => setI(i - 1)}
            className="pro-btn bg-slate-100 text-[#0b1b46] disabled:opacity-40 flex items-center gap-1">
            <ChevronRight className="rtl:block hidden" size={18} /><ChevronLeft className="rtl:hidden" size={18} />{t("back")}
          </button>
          <div className="flex gap-1">
            {slides.map((_, k) => (
              <button key={k} onClick={() => setI(k)} data-testid={`slide-dot-${k}`}
                className="w-2.5 h-2.5 rounded-full transition-colors"
                style={{ background: k === i ? "var(--pro-secondary)" : "#CBD5E1" }} />
            ))}
          </div>
          <button data-testid="next-slide-btn" disabled={i === slides.length - 1} onClick={() => setI(i + 1)}
            className="pro-btn text-white disabled:opacity-40 flex items-center gap-1"
            style={{ background: "var(--pro-primary)" }}>{t("next")}</button>
        </div>
      </div>
    </div>
  );
}
